/**
 * iter918 — "Tailor Pickups Today" dashboard card. Lists pos_orders whose
 * metadata.ready_date is today or overdue. Rows open a detail dialog with
 * the full order (items, measurements, photo). Renders nothing when there
 * are no due pickups, so non-tailor tenants never see it.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Scissors, Phone, CalendarClock } from 'lucide-react';
import { tailorReceiptLines } from '@/lib/tailorReceipt';

export const TailorPickupsCard = () => {
  const [data, setData] = useState(null);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    api.get('/tailor/pickups').then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const orders = data?.orders || [];
  if (!orders.length) return null;
  const today = data.date;

  return (
    <>
      <Card className="border-violet-200 dark:border-violet-900 bg-violet-50/40 dark:bg-violet-950/20" data-testid="tailor-pickups-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-outfit flex items-center gap-2">
            <Scissors size={16} className="text-violet-600" />
            Tailor Pickups Today / تسليمات الخياطة
            <Badge className="bg-violet-100 text-violet-700 border border-violet-200 text-[10px]" data-testid="tailor-pickups-count">{orders.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {orders.slice(0, 6).map((o) => {
            const md = o.metadata || {};
            const overdue = md.ready_date < today;
            return (
              <button
                key={o.id}
                type="button"
                className="w-full flex items-center gap-3 p-2 rounded-lg border border-border bg-white dark:bg-stone-900 hover:border-violet-300 transition-colors text-left"
                onClick={() => setViewing(o)}
                data-testid={`tailor-pickup-row-${o.order_number}`}
              >
                <span className="bg-violet-100 text-violet-700 font-bold rounded px-2 py-0.5 text-xs shrink-0">#{o.order_number}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-xs font-medium truncate">{md.customer_name || o.customer_name || 'Customer'}</span>
                  {md.customer_phone && <span className="block text-[10px] text-muted-foreground truncate"><Phone size={9} className="inline mr-0.5" />{md.customer_phone}</span>}
                </span>
                <span className="text-right shrink-0">
                  <Badge className={`text-[10px] border ${overdue ? 'bg-red-100 text-red-700 border-red-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
                    {overdue ? `Overdue / متأخر · ${md.ready_date}` : 'Today / اليوم'}
                  </Badge>
                </span>
              </button>
            );
          })}
          {orders.length > 6 && <p className="text-[10px] text-muted-foreground text-center">+{orders.length - 6} more due</p>}
        </CardContent>
      </Card>

      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }}>
        <DialogContent className="max-w-md rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="tailor-pickup-detail-dialog">
          {viewing && (() => {
            const md = viewing.metadata || {};
            const tailor = tailorReceiptLines(md);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-outfit flex items-center gap-2">
                    <Scissors size={18} className="text-violet-600" />Order #{viewing.order_number}
                  </DialogTitle>
                </DialogHeader>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Customer / العميل</span><span className="font-medium">{md.customer_name || viewing.customer_name || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Phone / الجوال</span><span className="font-medium">{md.customer_phone || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground"><CalendarClock size={12} className="inline mr-1" />Ready Date / تاريخ التسليم</span><span className="font-medium">{md.ready_date || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-bold">{Number(viewing.total || 0).toFixed(2)}</span></div>
                </div>
                <div className="border-t pt-2">
                  <p className="text-xs font-bold mb-1">Items</p>
                  {(viewing.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between text-xs py-0.5">
                      <span>{it.quantity || 1}× {it.name}</span>
                      <span>{Number(it.subtotal || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {tailor && (
                  <div className="border-t pt-2" data-testid="tailor-pickup-measurements">
                    <p className="text-xs font-bold mb-1">{tailor.title}</p>
                    {tailor.rows.map((r, i) => (
                      <div key={i} className="flex justify-between text-xs py-0.5">
                        <span className="text-muted-foreground">{r.label}</span>
                        <span className="font-medium">{r.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                {md.photo_url && (
                  <img src={md.photo_url} alt="Measurement sheet" className="w-full rounded-xl border object-contain max-h-56" data-testid="tailor-pickup-photo" />
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TailorPickupsCard;
